<template>
  <div ref="editorElement" class="tiptap-container" @click="setFocused($event)">
    <div v-if="editor">
      <ToolBar
        v-if="Array.isArray(toolbarOptions) && isFocused"
        :editor="editor as Editor"
        :is-focused="isFocused"
        :height-of-element="heightOfElement"
        :positions="position"
        :config="config"
        :layout-options="toolbarOptions[0]"
        :inverse="inverse"
        :center="config.center"
      />
      <EditorContent
        :editor="editor as Editor"
        class="tiptap-editor"
        :style="editorStyle"
      />
      <MergetagModal
        v-if="config.mergeTags"
        ref="mergetagModal"
        :merge-tags="config.mergeTags"
        :inverse="inverse"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import IEditorConfig from "../types/IEditorConfig";
import ToolBar from "./components/ToolBar.vue";
import suggestion from "./components/MTExtension/suggestion";
import { MergeTag } from "./components/MTExtension/mergetag";
import { MergetagSuggestion } from "./components/MTExtension/mergeTagSuggestion";
import { CustomItalic } from "./components/ToolBar/Italic/CustomItalic";
import {
  CustomOrderedList,
  CustomBulletList,
  CustomLists,
} from "./components/ToolBar/Lists/customLists";
import { CustomTextAlign } from "./components/ToolBar/TextAlign/customTextAlign";
import MergetagModal from "./components/MTExtension/MergetagModal.vue";
import {
  GenericBlockElementBlock,
  GenericBlockElementInline,
  GenericInlineElement,
  Image,
  Video,
  Audio,
  Area,
} from "./components/htmlEditor/otherHtmlTags";

import { getCopiedElement } from "@topol/utils";

import Link from "@tiptap/extension-link";
import ListKeymap from "@tiptap/extension-list-keymap";
import { TextStyleKit } from "@tiptap/extension-text-style";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { EditorContent, Editor } from "@tiptap/vue-3";
import { Editor as EditorType, Node } from "@tiptap/core";

// Custom Paragraph pro inline režim - renderuje jako <div> místo <p>
const DivParagraph = Node.create({
  name: "paragraph",
  priority: 1000,
  group: "block",
  content: "inline*",
  parseHTML() {
    return [{ tag: "div" }, { tag: "p" }];
  },
  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    return ["div", HTMLAttributes, 0];
  },
});

import { onClickOutside } from "@vueuse/core";
import {
  onBeforeUnmount,
  watch,
  computed,
  onMounted,
  ref,
  StyleValue,
} from "vue";
import mitt from "mitt";

// Props
const props = withDefaults(
  defineProps<{
    selectAll?: boolean;
    toolbar?: string[] | string | false;
    editorStyle?: StyleValue | Record<string, any>;
    config: IEditorConfig;
    inverse?: boolean;
    inline?: boolean;
  }>(),
  {
    inverse: true,
  },
);

const modelValue = defineModel<string>({
  type: String,
  required: true,
});

const mtStart = computed(() => {
  return props.config.mergeTags?.syntax?.start || "*|";
});

const editor = ref<EditorType | null>(null);
const editorElement = ref();
const isFocused = ref(false);
const wasWrapped = ref(false);
const mergetagModal = ref();
const position = ref({
  top: "0px",
  left: "0px",
  bottom: "auto",
  right: "auto",
});
const heightOfElement = ref(0);

const emitter = mitt();

// @ts-ignore - mitt typing issue with custom event types
emitter.on(
  "selectNewMT",
  async (data: {
    oldTag: string;
    resolve: (value: string | false) => void;
  }) => {
    if (!mergetagModal.value) {
      data.resolve(false);
      return;
    }
    const newTag = await mergetagModal.value.openModal(data.oldTag);
    data.resolve(newTag);
  },
);

const localValue = computed({
  get() {
    return wrapBareText(modelValue.value);
  },
  set(e: string) {
    const unwrapped = unwrapDiv(e);
    modelValue.value = unwrapped;
  },
});

const toolbarOptions = computed(() => {
  if (
    props.selectAll ||
    props.toolbar === false ||
    (props.toolbar && props.toolbar.length === 0)
  ) {
    return false;
  }
  if (props.toolbar && Array.isArray(props.toolbar)) {
    return props.toolbar;
  }
  if (props.toolbar && typeof props.toolbar === "string") {
    return props.toolbar.split("|").map((row) => row.trim());
  }
  return [
    "blocks fontfamily fontsizeinput bold italic underline strikethrough  removeformat nonbreaking| alignleft aligncenter alignright   alignjustify link numlist bullist forecolor backcolor  mergefields emoticons code more",
  ];
});

onClickOutside(
  editorElement,
  (ev) => {
    const html = document.querySelector("html");
    if (!("pointerType" in ev) || !ev.pointerType || !isFocused.value || ev.target === html) return;
    isFocused.value = false;
  },
  {
    ignore: [
      ".tiptap-toolbar",
      ".tiptap-toolbar *",
      "#tiptap-link-modal",
      "#tiptap-link-modal *",
      "#tiptap-mt-modal",
      "#tiptap-mt-modal *",
      "#tiptap-html-editor-modal",
      "#tiptap-html-editor-modal *",
      ".tiptap-mt-drop",
      ".tiptap-mt-drop *",
      ".tiptap-mt-drop-item",
    ],
    controls: true,
  },
);

function setToolbarPosition() {
  if (!editorElement.value) return;
  const boundingRect = editorElement.value.getBoundingClientRect();
  if (props.config.position) {
    position.value = { ...props.config.position };
  } else {
    position.value = {
      top: `${boundingRect.top - 5}px`,
      left: `${boundingRect.left + (boundingRect.right - boundingRect.left) / 2}px`,
      bottom: "auto",
      right: "auto",
    };
  }
  heightOfElement.value = boundingRect.height;
}

const focus = () => {
  if (!editor.value) return;
  // Only move the caret when entering the editor from an unfocused state (e.g.
  // clicking the button's padding/chrome rather than its text). Once the editor
  // is already focused, the browser has placed the caret where the user clicked
  // (or made a selection) — re-running focus("end") would yank it to the end and
  // collapse any selection, so we leave the native caret/selection untouched.
  if (!editor.value.isFocused) {
    editor.value.commands.focus("end");
  }
  isFocused.value = true;
};

function setFocused(e: any) {
  if (!editor.value) return;
  if (e.target && e.target.classList.contains("tiptap-editor")) {
    focus();
  }
  if (isFocused.value) return;
  isFocused.value = true;
  setToolbarPosition();
}

function wrapBareText(html: string): string {
  // First remove &nbsp; from empty blocks (came from output)
  const cleaned = removeNbspFromBlocks(html);
  const trimmed = cleaned.trim();

  if (!trimmed) {
    if (props.inline) {
      wasWrapped.value = true;
      return "<div></div>";
    }
    wasWrapped.value = false;
    return "<p></p>";
  }

  // Regex pro detekci, jestli začíná block elementem
  const blockElements =
    /^<(div|p|h[1-6]|ul|ol|li|blockquote|pre|table|section|article|header|footer|nav|aside|figure|figcaption)/i;

  if (blockElements.test(trimmed)) {
    wasWrapped.value = false;
    return trimmed; // Už má blokový element
  }

  // Jinak obal
  wasWrapped.value = true;
  return `<div>${trimmed}</div>`;
}

// Replace empty block elements with &nbsp; to preserve visual space (for output)
function addNbspToEmptyBlocks(html: string): string {
  return html.replace(/<(p|div|h[1-6])([^>]*)><\/\1>/gi, "<$1$2>&nbsp;</$1>");
}

// Remove &nbsp; from blocks that contain only &nbsp; (for editor input)
function removeNbspFromBlocks(html: string): string {
  return html.replace(
    /<(p|div|h[1-6])([^>]*)>(&nbsp;|\u00A0)<\/\1>/gi,
    "<$1$2></$1>",
  );
}

function unwrapDiv(html: string): string {
  const trimmed = html.trim();

  // Pokud je prázdný a byl obalený, vrať prázdný div
  if (trimmed == "<p></p>" && wasWrapped.value) {
    return "";
  }

  // Pokud je obsah pouze v jednom <div> bez dalších bloků
  const regex = /^<div>((?:(?!<div|<\/div>).)*)<\/div>$/s;
  const match = trimmed.match(regex);

  if (match && match[1]) {
    const inner = match[1];

    // Pokud uvnitř jsou inline elementy nebo text, odbal
    const hasBlockElements =
      /<(p|h[1-6]|ul|ol|blockquote|pre|table|section|figure|figcaption)/i.test(
        inner,
      );

    if (!hasBlockElements) {
      return addNbspToEmptyBlocks(inner);
    }
  }

  return addNbspToEmptyBlocks(html);
}

const CustomLink = Link.extend({
  addAttributes() {
    return {
      // @ts-ignore - TipTap's parent() method exists at runtime but not in types
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("style"),
        renderHTML: (attributes: any) => {
          if (!attributes.style) {
            return {};
          }
          return { style: attributes.style };
        },
      },
    };
  },
});

watch(localValue, (value) => {
  if (!editor.value) return;
  setToolbarPosition();
  const isSame = editor.value.getHTML() === value;
  if (isSame) {
    return;
  }

  editor.value.commands.setContent(value, {
    emitUpdate: false,
  });
});

watch(editorElement, (el) => {
  if (!el) return;
  setToolbarPosition();
});

onMounted(() => {
  editor.value = new Editor({
    // Activate TipTap's built-in text-direction core extension. "auto" makes the
    // browser's Unicode Bidi Algorithm set each block's direction from its first
    // strong character (Arabic/Hebrew => RTL, Latin => LTR). The extension writes
    // a `dir` attribute onto every block node — including the custom DivParagraph —
    // which overrides the inline `direction: ltr` inherited from the MJML column
    // wrapper. Any block that already carries an explicit dir (e.g. ar/he seeded
    // dir="rtl") keeps its value.
    textDirection: "auto",
    editorProps: {
      attributes: {
        class: "focus:outline-none",
      },
      handleKeyDown(view, event) {
        if (event.key === "Backspace") {
          const { state } = view;
          const { $from, empty } = state.selection;

          if (!empty) return false;

          const doc = state.doc;
          if (doc.childCount === 1) {
            const firstChild = doc.firstChild;
            if (!firstChild) return false;

            // Už je prázdný - zůstaň
            if (firstChild.content.size === 0) {
              return true;
            }

            // Má jen 1 znak a kurzor je na pozici pro smazání toho znaku
            // (content.size === 1 znamená 1 znak pro textový obsah)
            if (firstChild.textContent.length === 1 && $from.pos === 2) {
              // Smažeme obsah, ale zachováme blok
              const tr = state.tr.delete(1, 2);
              view.dispatch(tr);
              return true;
            }
          }
        }
        return false;
      },
      handlePaste(view, event, slice) {
        if (!event.clipboardData) {
          return false;
        }
        const data = event.clipboardData.getData("text");

        if (
          data.includes("<img") ||
          (data.includes("{") && data.includes("}") && getCopiedElement(data))
        ) {
          return true;
        }
        return false;
      },
    },
    extensions: [
      StarterKit.configure({
        italic: false,
        trailingNode: false,
        link: false,
        underline: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        listKeymap: false,
        paragraph: props.inline ? false : undefined,
      }),
      ...(props.inline ? [DivParagraph] : []),
      MergeTag.configure({
        syntax: props.config.mergeTags?.syntax || { start: "*|", end: "|*" },
        patterns: props.config.mergeTags?.patterns || {},
        // Pass getters so node views resolve names against the current catalog
        // rather than a snapshot frozen at editor-creation time.
        flatMergeTags: () => props.config.mergeTags?.flatMergeTags || [],
        showTagText: () => props.config.mergeTags?.showTagText ?? false,
        emitter: emitter,
      }),
      CustomLink.configure({
        openOnClick: false,
        defaultProtocol: "https",
        protocols: ["http", "https", "mailto", "tel"],
      }),
      CustomItalic,
      MergetagSuggestion.configure({
        suggestion: suggestion(
          mtStart.value,
          props.config.mergeTags?.flatMergeTags || [],
        ),
      }),
      Underline,
      CustomTextAlign.configure({
        types: [
          "heading",
          "paragraph",
          "orderedList",
          "bulletList",
          "listItem",
          "genericBlockBlock",
          "genericBlockInline",
        ],
      }),
      CustomBulletList,
      CustomOrderedList,
      ListKeymap,
      CustomLists,
      TextStyleKit,
      GenericBlockElementBlock,
      GenericBlockElementInline,
      GenericInlineElement,
      Image,
      Video,
      Audio,
      Area,
    ],
    content: localValue.value,
    onUpdate: () => {
      if (!editor.value) return;
      if (editor.value.getText() === "") {
        localValue.value = "";
        return;
      }
      localValue.value = editor.value.getHTML();
    },
  });
  window.addEventListener("resize", setToolbarPosition);
  window.addEventListener("scroll", setToolbarPosition);
  const mainScrollArea = document.getElementById("main-scroll-area");
  if (mainScrollArea) {
    mainScrollArea.addEventListener("scroll", setToolbarPosition);
  }
  setToolbarPosition();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", setToolbarPosition);
  window.removeEventListener("scroll", setToolbarPosition);
  const mainScrollArea = document.getElementById("main-scroll-area");
  if (mainScrollArea) {
    mainScrollArea.removeEventListener("scroll", setToolbarPosition);
  }
  if (!editor.value) return;
  editor.value.destroy();
});

defineExpose({
  focus,
});
</script>

<style>
.tiptap-editor .ProseMirror {
  word-wrap: initial !important;
  white-space: initial !important;
}

.autocomplete-wrapper {
  position: absolute;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  z-index: 1000;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}
.autocomplete {
  list-style: none;
  margin: 0;
  padding: 0;
}
.autocomplete-item {
  padding: 6px 10px;
  cursor: pointer;
}
.autocomplete-item:hover {
  background: #eef;
}
</style>
